import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Handle forced logout when token expires (401 from API)
  const handleUnauthorized = useCallback(() => {
    setUser(null);
    setSessionExpired(true);
    localStorage.removeItem('user_data');
    sessionStorage.removeItem('user_data');
  }, []);

  useEffect(() => {
    // Register the unauthorized handler with the API service
    apiService.setUnauthorizedHandler(handleUnauthorized);

    // Check for existing token and user data
    const token = localStorage.getItem('auth_token');
    const userData = localStorage.getItem('user_data') || sessionStorage.getItem('user_data');
    
    if (token && userData) {
      try {
        apiService.setToken(token);
        setUser(JSON.parse(userData));
      } catch (e) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        sessionStorage.removeItem('user_data');
      }
    }
    setIsLoading(false);
  }, [handleUnauthorized]);

  const login = async (email, password, rememberMe) => {
    try {
      const response = await apiService.login(email, password);
      
      if (response.success && response.data) {
        const userData = {
          email: response.data.user?.email || email,
          name: response.data.user?.firstname 
            ? `${response.data.user.firstname} ${response.data.user.lastname || ''}`.trim()
            : response.data.user?.fullname || email.split('@')[0],
          role: response.data.user?.role || 'superAdmin',
          id: response.data.user?._id,
        };
        
        setUser(userData);
        
        if (rememberMe) {
          localStorage.setItem('user_data', JSON.stringify(userData));
        } else {
          sessionStorage.setItem('user_data', JSON.stringify(userData));
        }
        
        return { success: true };
      }
      
      return { success: false, error: response.message || 'Login failed' };
    } catch (error) {
      return { 
        success: false, 
        error: error.message || 'Invalid email or password' 
      };
    }
  };

  const logout = () => {
    apiService.logout();
    setUser(null);
    setSessionExpired(false);
    localStorage.removeItem('user_data');
    sessionStorage.removeItem('user_data');
  };

  // Clear the session expired flag (used after showing the message)
  const clearSessionExpired = () => {
    setSessionExpired(false);
  };

  // SuperAdmin always has edit access
  const canEdit = () => {
    return user?.role === 'superAdmin' || user?.role === 'edit';
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout, canEdit, sessionExpired, clearSessionExpired }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
